import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import { Button } from '@mui/material';
import Table from '@mui/material/Table';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { TAB_QUERY } from 'src/utils/common-types';
import { hasPermission } from 'src/utils/has-permissions';
import logToucodeEvent, { APP_EVENTS } from 'src/utils/log-google-events';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteBarDispatchOrder,
  getBarDispatchOrders,
  removeBarDispatchOrderItem,
} from 'src/redux/slices/bar-dispatch-order';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  BAR_DISPATCH_ORDERS_STATUS_OPTIONS,
  BarDispatchOrdersStatus,
  IBarDispatchOrderListItem,
  IBarDispatchOrdersTableFilters,
  IBarDispatchOrdersTableFilterValue,
  IBarDispatchOrdersTableSort,
} from 'src/types/bar-dispatches';

import BarDispatchTableRow from '../bar-dispatch-table-row';
import BarDispatchTableToolbar from '../bar-dispatch-table-toolbar';

const STATUS_OPTIONS = BAR_DISPATCH_ORDERS_STATUS_OPTIONS;

const TABLE_HEAD = [
  { id: 'transactionCode', label: 'PO #', align: 'left', primary: true },
  { id: 'transactionType', label: 'Dispatch Type', align: 'left', primary: true },
  { id: 'paymentStatus', label: 'Payment Status', align: 'left', primary: true },
  { id: 'purchaseOrDispatchDate', label: 'Dispatch Date', align: 'left', primary: true },
  { id: 'updatedBy', label: 'Updated By', align: 'left', primary: true },
  { id: 'updatedAt', label: 'Updated At', align: 'left', primary: true },
  { id: '', primary: true },
];

const defaultFilters: IBarDispatchOrdersTableFilters = {
  generalSearch: '',
  dispatchDate: null,
  type: null,
  paymentStatus: null,
};

const defaultSortBy: IBarDispatchOrdersTableSort = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function BarDispatchOrdersListView() {
  const { status, transactions, error } = useAppSelector((state) => state.barDispatchOrders);

  const [searchParams, setSearchParams] = useSearchParams();

  const { user: authUser } = useAuthContext();

  const currentTab = searchParams.get(TAB_QUERY);

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const [statusTab, setStatusTab] = useState<string>(
    currentTab || BarDispatchOrdersStatus.CONFIRMED
  );

  const handleFilters = useCallback(
    (name: string, value: IBarDispatchOrdersTableFilterValue) => {
      table.onResetPage();

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.bar.dispatch.edit(id));
    },
    [router]
  );

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      setStatusTab(newValue);
      setSearchParams({ [TAB_QUERY]: newValue });
    },
    [setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
    setSearchParams({ [TAB_QUERY]: BarDispatchOrdersStatus.CONFIRMED });
  }, [setSearchParams]);

  const fetchPaginatedBarDispatchOrders = useCallback(() => {
    dispatch(
      getBarDispatchOrders({
        page,
        limit: rowsPerPage,
        sortBy: sort.sortBy,
        sort: sort.order,
        generalSearch: filters.generalSearch,
        status: statusTab === BarDispatchOrdersStatus.ALL ? null : statusTab,
        dispatchDate: filters.dispatchDate ?? null,
        paymentStatus: filters.paymentStatus ?? null,
        dispatchType: filters.type ?? null,
      })
    );

    if (page > 1) {
      logToucodeEvent(APP_EVENTS.TABLE_PAGINATION_MORE_THAN_TWO, {
        page,
        rowsPerPage,
      });
    }
  }, [dispatch, page, rowsPerPage, sort, filters, statusTab]);

  const handleDeleteRow = useCallback(
    async (item: IBarDispatchOrderListItem, index: number) => {
      dispatch(removeBarDispatchOrderItem({ itemId: item._id }));

      try {
        await dispatch(deleteBarDispatchOrder({ item, index })).unwrap();

        enqueueSnackbar('bar dispatch order deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchPaginatedBarDispatchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedBarDispatchOrders]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Bar Dispatch Orders"
        links={[]}
        action={
          hasPermission(
            authUser,
            'any',
            AuthPermissions.CREATE_BAR_INVENTORY_TRANSACTION,
            AuthPermissions.ADMIN_PERMISSION,
            AuthPermissions.SUPER_ADMIN_PERMISSION
          ) && (
            <Button
              component={RouterLink}
              href={paths.dashboard.bar.dispatch.new}
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
            >
              New Dispatch Order
            </Button>
          )
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <Tabs
          value={statusTab}
          onChange={handleFilterStatus}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {STATUS_OPTIONS.map((tab) => (
            <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <BarDispatchTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD.filter((headerItem) => {
                  if (statusTab !== BarDispatchOrdersStatus.DRAFT) return true;
                  return headerItem.id !== 'transactionCode';
                }).filter(({ primary }) => {
                  if (settings.themeLayout === 'vertical') {
                    return primary;
                  }
                  return true;
                })}
                rowCount={transactions.transactions.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {transactions?.transactions.map(
                      (row: IBarDispatchOrderListItem, index: number) => (
                        <BarDispatchTableRow
                          key={row._id}
                          isDraft={statusTab === BarDispatchOrdersStatus.DRAFT}
                          row={row}
                          selected={table.selected.includes(row._id)}
                          onDeleteRow={() => handleDeleteRow(row, index)}
                          onEditRow={() => handleEditRow(row._id)}
                        />
                      )
                    )}
                  </>
                )}

                {status !== 'failure' && transactions.count === 0 && (
                  <TableNoData
                    message="No dispatch orders found"
                    notFound={transactions.count === 0}
                  />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={transactions.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
